import { useApp } from '../i18n/AppContext';
import { Campaign, DEMO_YEAR } from '../store/seed';
import { Post } from '../types';

const NOVEMBER_MONTH_INDEX = 10;
const DECEMBER_MONTH_INDEX = 11;
const ENGLISH_DEMO_DATE_IN_TASK_TEXT = /\b(\d{1,2}) (Nov|Dec)\b/g;

export const campaignStatusClass: Record<Campaign['status'], string> = {
  Draft: 'neutral',
  Planning: 'ready',
  'Plan ready': 'review',
  'Ideas ready': 'review',
  Active: 'scheduled',
  'Needs your input': 'input',
  Paused: 'neutral',
  Finished: 'published',
};

export const channelIconName: Record<string, string> = {
  Instagram: 'FiInstagram',
  Facebook: 'FiFacebook',
  TikTok: 'FiVideo',
  LinkedIn: 'FiLinkedin',
};

export function localizeDemoDatesInVariables(
  taskTextVariables: Record<string, string> | undefined,
  language: string
) {
  if (!taskTextVariables) return taskTextVariables;
  const dateFormatter = new Intl.DateTimeFormat(language, { day: 'numeric', month: 'short' });
  const showEnglishDemoDatesInViewerLanguage = (value: string) =>
    value.replace(
      ENGLISH_DEMO_DATE_IN_TASK_TEXT,
      (_, dayOfMonth: string, monthAbbreviation: string) =>
        dateFormatter.format(
          new Date(
            DEMO_YEAR,
            monthAbbreviation === 'Nov' ? NOVEMBER_MONTH_INDEX : DECEMBER_MONTH_INDEX,
            Number(dayOfMonth)
          )
        )
    );
  return Object.fromEntries(
    Object.entries(taskTextVariables).map(([variableName, value]) => [
      variableName,
      showEnglishDemoDatesInViewerLanguage(value),
    ])
  );
}

export function useDayFormat() {
  const { language } = useApp();
  return (day: number) =>
    new Intl.DateTimeFormat(language, { day: 'numeric', month: 'short' }).format(
      new Date(DEMO_YEAR, NOVEMBER_MONTH_INDEX, day)
    );
}

export function campaignDates(
  campaign: Pick<Campaign, 'start' | 'end'>,
  formatDay: (day: number) => string,
  translate: (key: string, variables?: Record<string, string | number>) => string
) {
  if (!campaign.start) return translate('Always-on');
  if (!campaign.end || campaign.end === campaign.start) return formatDay(campaign.start);
  return `${formatDay(campaign.start)} – ${formatDay(campaign.end)}`;
}

export function campaignCounts(posts: Post[], campaign: Campaign) {
  const own = posts.filter((post) => post.campaign === campaign.key);
  const countWithStatus = (statuses: Post['status'][]) =>
    own.filter((post) => statuses.includes(post.status)).length;
  return {
    total: own.length,
    published: countWithStatus(['Published']),
    scheduled: countWithStatus(['Scheduled']),
    review: countWithStatus(['Needs your review']),
    working: countWithStatus(['AMY is working', 'Planned']),
    input: countWithStatus(['Needs your input']),
    own,
  };
}
